import {FormMode, RequestParams} from '@ngx-ivengi/crud';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {UIRouter} from '@uirouter/angular';
import {assignmentNumberingType, FormWizardService} from '@brands-compart/form-wizard';
import {Observable} from 'rxjs/Observable';
import {User} from '../user/user.model';
import {Room} from '../room/room.model';
import {Floor} from '../floor/floor.model';
import {Channel} from './channel.model';
import {UserService} from '../user/user.service';
import {AssignmentService} from '../assignment/assignment.service';
import {Assignment} from '../assignment/assignment.model';
import {Building} from '../building/building.model';
import {CombiFormComponent} from '../shared/combi-form.component';
import {TranslateService} from '@ngx-translate/core';
import {BuildingService} from '../building/building.service';
import {FloorService} from '../floor/floor.service';
import {ContactService} from '../contact/contact.service';
import {FilterParam} from '@ngx-ivengi/crud/request-params';
import {Contact} from '../contact/contact.model';
import {RoomService} from '../room/room.service';
import {ChannelService} from './channel.service';
import {RoutePrefix} from '../shared/route-prefix';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/first';

@Component({
  selector: 'app-channel-form',
  templateUrl: './channel-form.component.html'
})
export class ChannelFormComponent extends CombiFormComponent implements OnInit, OnDestroy {

  public assignmentType: string;
  public stateIdParam: string = 'channelId';
  public formTitle: string;
  public channel: Channel = new Channel();
  public channelFields: any = [];
  public formChannelValues;
  public customers: Contact[] = [];
  public buildings: Building[] = [];
  public floors: Floor[] = [];
  public initialized: boolean = false;
  public routePrefix: RoutePrefix[] = [];
  public activeTab: string = (localStorage.getItem('channelTab')) ? localStorage.getItem('channelTab') : 'channel';
  public labelPlaceholder: string = '';
  public assignment: Assignment;
  public viewMode: boolean;
  public forceEditMode: boolean = false;

  private rooms: Room[] = [];
  private mechanics: User[] = [];
  private formWizardService: FormWizardService;

  private roomResolve;
  private labelResolve;

  constructor(
    protected router: UIRouter,
    private userService: UserService,
    private contactService: ContactService,
    private buildingService: BuildingService,
    private floorService: FloorService,
    private roomService: RoomService,
    private assignmentService: AssignmentService,
    private channelService: ChannelService,
    private translate: TranslateService
  ) {
    super(router);

    this.formWizardService = new FormWizardService();
  }

  public ngOnInit(): void {

    this.forceEditMode = (this.router.stateService.params.forceEdit === 'true');

    this.translate.get('channel.form.field.labelPlaceholder').subscribe((translation: string) => {
      this.labelPlaceholder = translation;
    });

    super.ngOnInit();

    this.routePrefix = [
      {
        entity: Assignment.name.toLowerCase(),
        entityId: this.router.stateService.params.assignmentId
      },
      {
        entity: Channel.name.toLowerCase(),
        entityId: this.router.stateService.params.channelId
      }
    ];

    this.channelService.setRoutePrefix(`assignment/${this.router.stateService.params.assignmentId}`);
    this.channel.setAssignmentId(this.router.stateService.params.assignmentId);

    switch(this.formMode) {
      case this.FormMode.ADD: {
        this.translate.get('channel.form.add.blockTitle').subscribe((translation) => {
          this.formTitle = translation;
        });
      } break;

      case this.FormMode.UPDATE: {
        this.channelService.get(this.router.stateService.params[this.stateIdParam]).subscribe((channel: Channel) => {
          this.channel = channel;
          this.buildingChange();
        });

        this.translate.get('channel.form.edit.blockTitle').subscribe((translation) => {
          this.formTitle = translation;
        });
      } break;
    }

    const promises = [];

    promises.push(new Promise((resolve) => {
      this.roomResolve = resolve;
    }));

    if(this.formMode === this.FormMode.ADD) {
      promises.push(new Promise((resolve) => {
        this.labelResolve = resolve;
      }));
    }

    promises.push(new Promise((resolve) => {
      this.assignmentService.get(this.router.stateService.params.assignmentId).subscribe((assignment: Assignment) => {
        this.assignmentType = assignment.type.toLowerCase();
        this.channel.setAssignmentType(assignment.type);
        resolve();
      });
    }));

    const routePrefix = `${Assignment.name.toLowerCase()}/${this.router.stateService.params.assignmentId}`;
    this.contactService.setRoutePrefix(routePrefix);

    this.buildingService.setRoutePrefix(routePrefix);

    promises.push(new Promise((resolve) => {
      const customerFilter: FilterParam = {
        id: 0,
        key: 'type',
        operator: 'eq',
        value: 'CUSTOMER'
      };
      const customerRequestParams = new RequestParams();
      customerRequestParams
        .addFilterParam(customerFilter)
        .pagination.limit = 0;
      this.contactService.getAll(customerRequestParams).subscribe((customers: Contact[]) => {
        this.customers = customers;
        resolve(customers);
      });
    }));

    promises.push(new Promise((resolve) => {
      const buildingRequestParams = new RequestParams();
      buildingRequestParams
        .pagination.limit = 0;
      this.buildingService.getAll(buildingRequestParams).subscribe((buildings: Building[]) => {
        this.buildings = buildings;

        // select default first building
        if(this.formMode === FormMode.ADD) {
          this.channel
            .setBuilding(this.buildings[0])
            .setBuildingId(this.buildings[0].buildingId);

          this.buildingChange();
        }
        resolve(buildings);
      });
    }));

    promises.push(new Promise((resolve) => {
      const mechanicRequestParams = new RequestParams();
      mechanicRequestParams
        .addFilterParam({
          id: 0,
          key: 'roles',
          operator: 'eq',
          value: 'MECHANIC',
          filterFn: 'substringof',
          filterOutcome: 'true'
        })
        .pagination.limit = 0;

      this.userService.getAll(mechanicRequestParams).subscribe((mechanics: User[]) => {
        this.mechanics = mechanics.map(mechanic => {
          if (!mechanic.enabled) {
            mechanic.username = `${mechanic.username} (${this.translate.instant('user.disabled')})`;
          }
          return mechanic;
        });

        this.mechanics.sort((a, b) => {
          switch (true) {
            case a.enabled && !b.enabled: {
              return -1;
            }

            case !a.enabled && b.enabled: {
              return 1;
            }

            case a.enabled && b.enabled:
            default: {
              return 0;
            }
          }
        });
        resolve(mechanics);
      });
    }));


    promises.push(new Promise((resolve) => {
      this.assignmentService.get(this.router.stateService.params.assignmentId).subscribe((assignment: Assignment) => {
        this.assignment = assignment;
        resolve(assignment);
      });
    }));

    Promise.all(promises)
      .then(() => {
        const channelData = {
          customers: this.customers.sort((a: Contact, b: Contact) => a.referenceName.localeCompare(b.referenceName)),
          rooms: this.rooms,
          mechanics: this.mechanics,
          channel: this.channel,
          assignment: this.assignment,
          viewMode: (this.forceEditMode) ? false : (this.assignment.status === 'PROCESSED'),
        };

        this.formWizardService.initChannelField(channelData);
        this.formWizardService.channelFields.subscribe((fields) => {
          this.channelFields = fields;
        });
        this.formChannelValues = this.formWizardService.channelFormValues;
        this.initialized = true;
        this.viewMode = (this.forceEditMode) ? false : (this.assignment.status === 'PROCESSED');
      });
  }

  public ngOnDestroy(): void {
    this.channelService.clearRoutePrefix();
    this.buildingService.clearRoutePrefix();
    this.floorService.clearRoutePrefix();
    this.roomService.clearRoutePrefix();
    this.contactService.clearRoutePrefix();
  }

  public changeChannelField(evt: string, field: any) {
    this.formWizardService.setChannelField({
      key: `channel.${field.key}`,
      value: evt
    });
  }

  public buildingChange() {
    this.floorService.setRoutePrefix(`building/${this.channel.buildingId}`);
    this.floorService.getAll().subscribe((floors: Floor[]) => {
      this.floors = floors.sort((a: Floor, b: Floor) => a.number - b.number);
      const floorIds = floors.map((floor: Floor) => floor.id);
      // Always update floor so channel-drawing component can load related floor image
      if(!this.channel.floor || floorIds.indexOf(this.channel.floorId) === -1) {
        this.channel
          .setFloor(this.floors[0])
          .setFloorId(this.floors[0].id);
      }
      this.floorChange();
    });

    if(this.initialized) {
      const channelData = {
        assignment: this.assignment,
        customers: this.customers,
        rooms: this.rooms,
        channel: this.channel,
        mechanics: this.mechanics
      };

      this.formWizardService.initChannelField(channelData);

      this.formWizardService.channelFields.subscribe((fields) => {
        this.channelFields = fields;
      });

      this.formChannelValues = this.formWizardService.channelFormValues;
    }

  }

  public floorChange() {
    for(const floor of this.floors) {
      if(floor.id === this.channel.floorId) {
        this.channel.setFloor(floor);
      }
    }

    this.roomService.setRoutePrefix(`building/${this.channel.buildingId}`);

    const observables: Observable<any>[] = [
      this.roomService.getAll().first()
    ];

    if(this.labelResolve && this.assignment.numbering === assignmentNumberingType.AUTOMATIC) {
      observables.push(this.channelService.nextLabel((<string>this.channel.buildingId), (<string>this.channel.floorId)).first());
    }

    Observable.forkJoin(observables).subscribe((results) => {
      const rooms: Room[] = results[0];
      this.rooms = rooms;
      this.roomResolve(rooms);

      if(results.length === 2) {
        const nextLabel = results[1];
        this.channel.label = nextLabel;
        this.labelResolve(nextLabel);
      } else {
        if(this.labelResolve) {
          this.labelResolve(this.channel.label);
        }
      }

      if(this.initialized) {
        const channelData = {
          assignment: this.assignment,
          customers: this.customers,
          rooms: this.rooms,
          channel: this.channel,
          mechanics: this.mechanics
        };

        this.formWizardService.initChannelField(channelData);

        this.formWizardService.channelFields.subscribe((fields) => {
          this.channelFields = fields;
        });

        this.formChannelValues = this.formWizardService.channelFormValues;
      }

    });
  }

  public save() {
    switch(this.formMode) {
      case this.FormMode.ADD: {
        const channelData = {
          assignment: this.assignment,
          customers: this.customers,
          rooms: this.rooms,
          channel: this.channel,
          mechanics: this.mechanics
        };

        this.channelService.add(this.channel).subscribe((channel: Channel) => {
          const params = Object.assign({channelId: channel.id}, this.router.stateService.params);
          localStorage.setItem('channelTab', 'throughput');
          this.router.stateService.go(`assignment.${this.assignmentType}.edit.channel.edit`, params, {reload: true});
        });
      } break;

      case this.FormMode.UPDATE: {
        this.channelService.update(this.channel).subscribe((channel: Channel) => {
          // if force edit mode is true, change request came from building page. So redirect back to it after saving
          if (this.forceEditMode) {
            this.router.stateService.go('building.edit', { buildingId: this.channel.buildingId }, { reload: true });
          } else {
            this.router.stateService.go(`assignment.${this.assignmentType}.edit`, this.router.stateService.params, {reload: true});
          }
        });
      } break;
    }
  }

  public onTabSelect(tab) {
    localStorage.setItem('channelTab', tab);
  }
}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/channel/channel-form.component.ts